import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query';
import { queryKeyes } from 'shared/queryKeys';
import { Get } from '../../vesselMaster.hooks';
import { RootState } from 'index';
import { useDispatch, useSelector } from 'react-redux';
import { FormikProps, useFormik } from 'formik';
import { CardBody, Row, Col, Form, CardFooter, Button } from 'reactstrap';
import ErrorComponent from 'Components/ErrorComponent';
import FormValuesDebug from 'utils/debugTools/FormValuesDebug';
import { checkValidArray, handleServerResponse, isConfigurationButtonDisabled } from 'GenericForms/Helper';
import apiGlobal from 'global/api.global';
import { queryClient } from 'react-query/queryClient';
import VesselFuelTable from './VesselFuelTable';
import { VesselConfigrationTabs, VesselTypeConstant } from 'shared/constants';
import isEqual from "fast-deep-equal";
import Select from 'react-select';
import Message from 'Components/Message';
import FormLoading from 'Components/FormLoading';
import { getConfigurationLimitsAction } from 'Store/Generic/ReportingSlice';
import { FuelTypes as FuelTypesPrecedenceId } from '../../../shared/constants';
interface FuelConsumptionForm {
    [key: string]: string | number | boolean | Date | null;
}

interface VesselFuelConfigurationType {
    refreshVesselMachineries: number;
    setRefreshVesselMachineries: (value: number) => void;
    setCheckValuesBeforeSwitch: (value: boolean) => void;
    setTabName: (value: string) => void;
    setFormik?: (value: FormikProps<any>) => void;
    formik?: FormikProps<any>;
    VesselConfActiveTab: number;
    refreshForm: boolean;
}

const VesselFuelConfiguration = ({
    refreshVesselMachineries,
    setRefreshVesselMachineries,
    setCheckValuesBeforeSwitch,
    setTabName,
    setFormik,
    VesselConfActiveTab,
    refreshForm
}: VesselFuelConfigurationType) => {
    /** State variables start */
    const { VesselID, Vessels, ConfigurationLimits, reportingLoading } = useSelector((state: RootState) => state.Reporting);
    const vessel = Vessels.find((rec: any) => rec.id === VesselID);
    const [refreshKey, setRefreshKey] = useState<number>(0)
    const VesselFuelTypes = ConfigurationLimits?.vessel_fuel_information ?? [];
    const dispatch = useDispatch();
    /** State variables end */

    /** useQueries */
    /** Load fuel types */
    const {
        data: FuelTypes,
        isLoading: FuelTypesLoading,
        isError: FuelTypesError,
    } = useQuery(
        [queryKeyes.masters.fuelTypeMaster.key],
        async () => {
            return await Get(queryKeyes.masters.fuelTypeMaster.url());
        },
        {
            enabled: true,
            staleTime: Infinity,
        }
    );
    /** Load Fuel class from master */
    const {
        data: FuelClass, isLoading: FuelClassLoading, isError: FuelClassError
    }: { data: any[]; isLoading: any; isError: any } = useQuery(
        [queryKeyes.masters.FuelClassMaster.key, VesselID],
        async () => {
            return await Get(queryKeyes.masters.FuelClassMaster.url());
        },
        {
            enabled: true,
            staleTime: Infinity,
        }
    );
    /** useQueries end */

    // Filter distinct `fuel_class_precedence_id`
    const distinctFuelClasses = () => {
        let fuelClassIds: any = [];
        if (vessel?.vessel_type_precedence_id === VesselTypeConstant.LNG_CARRIER && !checkValidArray(VesselFuelTypes)) {
            const LNGCargoDetails = FuelTypes?.find((item: any) => item.precedence_id === FuelTypesPrecedenceId.LNG_CARGO);
            fuelClassIds.push({
                fuel_class_precedence_id: LNGCargoDetails?.fuel_class_precedence_id,
                fuel_class_name: LNGCargoDetails?.fuel_class_name
            })
        }
        VesselFuelTypes && VesselFuelTypes.forEach((fuel: any) => {
            // Check if the fuel_class_precedence_id is already in the fuelClassIds array
            const existing = fuelClassIds?.find((item: any) => item.fuel_class_precedence_id === fuel.fuel_class_precedence_id);
            // If not found, push the new fuel class
            if (!existing) {
                fuelClassIds.push({
                    fuel_class_precedence_id: fuel.fuel_class_precedence_id,
                    fuel_class_name: fuel.fuel_class_name
                });
            }
        });
        return fuelClassIds;
    };

    const handleRemove = async (actionMeta: any) => {
        // Find the removed option's precedence_id
        const removedPrecedenceId = actionMeta?.removedValue?.precedence_id;
        // Loop through vesselFuels and set selected to false for the corresponding fuel
        const updatedVesselFuels = VesselFuelFormik?.values.vesselFuels.map((fuel: any) => {
            if (fuel && removedPrecedenceId && fuel.fuel_class_precedence_id === removedPrecedenceId && fuel.selected === true) {
                // Set selected to false for the matching fuel
                fuel.selected = false;
            }
            return fuel;
        });
        // Update the vesselFuels with the new selected state
        VesselFuelFormik?.setFieldValue('vesselFuels', updatedVesselFuels);
        setRefreshKey(refreshKey + 1)
    }

    const updateFuelClass = async (selectedOptions: any) => {
        const arr = selectedOptions.map((option: any) => ({
            fuel_class_precedence_id: option.precedence_id,
            fuel_class_name: option.name
        }))
        VesselFuelFormik.setFieldValue(
            "fuel_class",
            arr
        );
        VesselFuelFormik.values.fuel_class = arr;
    }

    /** Assign initial values to fuel consumption Formik object */
    const getInitialValues = () => {
        let arr: FuelConsumptionForm[] = [];
        if (VesselFuelTypes && VesselFuelTypes?.length > 0) {
            if (arr?.length < FuelTypes?.length) {
                arr = VesselFuelTypes?.map((fuelType: FuelConsumptionForm) => ({
                    ...fuelType,
                    selected: true
                }))
                FuelTypes?.filter((item: FuelConsumptionForm) =>
                    !VesselFuelTypes.some((fuel: FuelConsumptionForm) => item?.id === fuel?.fuel_type)
                )?.forEach((fuelType: FuelConsumptionForm) => {
                    arr?.push({
                        fuel_name: '',
                        fuel_type_name: fuelType?.fuel_type_name,
                        fuel_type: fuelType?.id,
                        fuel_sub_type: 0,
                        selected: false,
                        vessel: VesselID,
                        fuel_class_precedence_id: fuelType.fuel_class_precedence_id
                    })
                })
            }
        } else {
            FuelTypes?.forEach((fuelType: FuelConsumptionForm) => {
                arr.push({
                    fuel_name: '',
                    fuel_type_name: fuelType?.fuel_type_name,
                    fuel_type: fuelType?.id,
                    fuel_sub_type: 0,
                    selected: (
                        vessel?.vessel_type_precedence_id === VesselTypeConstant.LNG_CARRIER &&
                        fuelType?.precedence_id === FuelTypesPrecedenceId.LNG_CARGO
                    ) ? true : false,
                    vessel: VesselID,
                    fuel_class_precedence_id: fuelType.fuel_class_precedence_id
                })
            })
        }
        return arr;
    }

    /** Vessel fuel configuration's formik object */
    const VesselFuelFormik: any = useFormik({
        enableReinitialize: true,
        initialValues:
        {
            fuel_class: distinctFuelClasses() as any,
            vesselFuels: getInitialValues()
        },
        onSubmit: async () => {
            VesselFuelFormik?.setSubmitting(true);
            let responseArray: any[] = [];

            VesselFuelFormik?.values?.vesselFuels?.forEach((value: FuelConsumptionForm) => {
                if (value?.id as number > 0) {
                    if (value?.selected === false) {
                        responseArray.push(apiGlobal.post(`vessel_fuel_information/fuel_type_bulk_delete/`, value));
                    }
                } else {
                    if (value?.selected === true) {
                        responseArray.push(apiGlobal.post(`/vessel_fuel_information/`, value));
                    }
                }
            });

            const res = await handleServerResponse(responseArray);
            if (res === true) {
                await queryClient.invalidateQueries(queryKeyes.vessel.fuel.key);
                dispatch(getConfigurationLimitsAction(VesselID as number));
                await queryClient.invalidateQueries(queryKeyes.vessel.ConfigurationList.key);
                await dispatch(getConfigurationLimitsAction(VesselID as number));
                setRefreshVesselMachineries(refreshVesselMachineries + 1);
            }
        }
    });

    const tabName = 'Vessel Fuel';
    const handleFormikStateChange = (values: any, initialValues: any) => {
        const areValuesEqual = isEqual(values, initialValues);
        setCheckValuesBeforeSwitch(areValuesEqual);
        setTabName(tabName);
        return areValuesEqual;
    };

    /** useEffect */
    useEffect(() => {
        if (VesselConfActiveTab === VesselConfigrationTabs.VESSEL_FUEL &&
            VesselFuelFormik.values.vesselFuels !== VesselFuelFormik.initialValues.vesselFuels
        ) {
            setFormik(VesselFuelFormik);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [VesselConfActiveTab, VesselFuelFormik.values]);
    useEffect(() => {
        VesselFuelFormik.initialValues.fuel_class = distinctFuelClasses();
        VesselFuelFormik.values.fuel_class = distinctFuelClasses();
        VesselFuelFormik.initialValues.vesselFuels = getInitialValues();
        VesselFuelFormik.values.vesselFuels = getInitialValues();
        setRefreshVesselMachineries(refreshVesselMachineries + 1);
        VesselFuelFormik.setValues({
            ...VesselFuelFormik.values,
            fuel_class: distinctFuelClasses()
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [VesselFuelTypes, FuelTypes, VesselID,])
    useEffect(() => {
        handleFormikStateChange(VesselFuelFormik.values.vesselFuels, VesselFuelFormik.initialValues.vesselFuels);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [VesselFuelFormik.values, VesselFuelFormik.initialValues]);
    /** useEffect end */

    if (FuelTypesLoading || reportingLoading || FuelClassLoading) {
        return <FormLoading />
    } else if (FuelTypesError || FuelClassError) {
        return <ErrorComponent message='Error loading required data!' />
    } else {
        return (
            <Form onSubmit={VesselFuelFormik?.handleSubmit} noValidate autoComplete="off">
                <CardBody className="p-0">
                    <h4 className="mb-3 navbar_menu">Vessel Fuel Settings</h4>
                    {!(FuelTypesLoading || FuelClassLoading) &&
                        !(FuelTypesError || FuelClassError) &&
                        // add multiselect dropdown
                        <React.Fragment key={refreshVesselMachineries}>
                            <Select
                                isMulti
                                name="fuel_class"
                                className='max-width-15 mb-2'
                                options={FuelClass}
                                getOptionLabel={(e: any) => e.name}
                                getOptionValue={(e: any) => e.precedence_id}
                                value={VesselFuelFormik?.values?.fuel_class?.map((fuelClass: any) => ({
                                    precedence_id: fuelClass.fuel_class_precedence_id,
                                    name: fuelClass.fuel_class_name
                                }))}
                                onChange={async (selectedOptions, actionMeta) => {
                                    await updateFuelClass(selectedOptions)
                                    await handleRemove(actionMeta)
                                }}
                                isClearable={false} // Disables the clear all behavior
                                placeholder="Select Fuel Class"
                                isDisabled={isConfigurationButtonDisabled(vessel)}
                            />
                            <Row>
                                {VesselFuelFormik?.values?.fuel_class?.length > 0 ? (
                                    VesselFuelFormik?.values?.fuel_class?.map((item: any, index: number) => (
                                        <Col md={3} className="px-0" key={`${refreshForm}-${index}`}>
                                            <VesselFuelTable
                                                fuel_class_precedence_id={item.fuel_class_precedence_id}
                                                fuel_class_name={item.fuel_class_name}
                                                refreshKey={refreshKey}
                                                formik={VesselFuelFormik}
                                                refreshForm={refreshForm}
                                            />
                                        </Col>
                                    ))
                                ) : (
                                    <Message message='Select a fuel class to display respective fuel types' type='warning' width='25rem' />
                                )}
                            </Row>
                        </React.Fragment>
                    }
                </CardBody>
                <CardFooter className='p-2 py-3 mb-3'>
                    <Row>
                        <Col className='pb-3'>
                            <Button
                                type="submit"
                                color='primary'
                                className='pos-end'
                                disabled={isConfigurationButtonDisabled(vessel) || VesselFuelFormik?.isSubmitting}
                            >
                                {VesselFuelFormik?.isSubmitting && <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />}
                                Save
                            </Button>
                        </Col>
                    </Row>
                </CardFooter>
                <FormValuesDebug values={[VesselFuelFormik?.values, VesselFuelFormik?.errors, VesselFuelFormik?.initialValues]} />
            </Form>
        )
    }
}

export default VesselFuelConfiguration